import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, CalendarRange, Search } from "lucide-react";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { Card } from "@shared/ui/containers/Card";
import { Fab } from "@shared/ui/primitives/Fab";
import { dropdownClassName } from "@shared/ui/primitives/Dropdown";
import { PillTabs } from "@shared/ui/lists/PillTabs";
import { useBrandImagery } from "@shared/hooks/useBrandImagery";
import { pageCopy } from "@shared/lib/pageCopy";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { fetchFacilities, type Facility } from "./api";

/** A facility may carry a category/type — read loosely (not in the base type). */
function categoryOf(f: Facility): string | undefined {
  const c =
    (f as unknown as { category?: string; type?: string }).category ??
    (f as unknown as { type?: string }).type;
  return c?.trim() || undefined;
}

/**
 * Facilities landing — the "Book" tab. Fancourt layout: a photo header,
 * a surveys-style search card, category chips, and a two-column grid of
 * image-led facility cards.
 */
export function Facilities(): JSX.Element {
  const copy = pageCopy().facilities;
  const navigate = useNavigate();
  const { heroImage } = useBrandImagery();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  const query = useQuery({
    queryKey: ["facilities"],
    queryFn: fetchFacilities,
  });
  const all = query.data ?? [];

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const f of all) {
      const c = categoryOf(f);
      if (c) set.add(c);
    }
    return ["All", ...[...set].sort()];
  }, [all]);

  const facilities = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter((f) => {
      if (category !== "All" && categoryOf(f) !== category) return false;
      if (!term) return true;
      return [f.name, f.description, f.location_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [all, search, category]);

  return (
    <div className="min-h-full bg-paper">
      {/* ── Photo header ─────────────────────────────────────────── */}
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
        backgroundImage={heroImage}
      />

      <div className="px-5 pt-4 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
        {/* ── Search card ────────────────────────────────────────── */}
        <Card noGrain className="mb-4 p-4">
          <p className="eyebrow mb-2.5">Search facilities</p>
          <label className="relative block">
            <Search
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
              strokeWidth={1.75}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={copy.searchPlaceholder}
              aria-label="Search facilities"
              className={dropdownClassName({ className: "pl-10 pr-3.5" })}
            />
          </label>
        </Card>

        {/* ── Category chips ─────────────────────────────────────── */}
        {categories.length > 1 ? (
          <PillTabs
            ariaLabel="Facility categories"
            options={categories.map((value) => ({ value, label: value }))}
            value={category}
            onChange={setCategory}
            className="pb-1"
          />
        ) : null}

        {/* ── Grid ───────────────────────────────────────────────── */}
        {query.isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-52" />
            <Skeleton className="h-52" />
            <Skeleton className="h-52" />
            <Skeleton className="h-52" />
          </div>
        ) : query.isError ? (
          <ErrorState onRetry={() => query.refetch()} />
        ) : facilities.length === 0 ? (
          <EmptyState
            icon={<CalendarRange className="h-7 w-7" strokeWidth={1.5} />}
            title={
              search || category !== "All"
                ? "No matches."
                : "No facilities yet."
            }
            description={
              search || category !== "All"
                ? "Try a different search or category."
                : "Your estate has not published any bookable facilities."
            }
          />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {facilities.map((facility) => (
              <FacilityCard
                key={facility.facility_id}
                facility={facility}
                onClick={() =>
                  navigate(`/app/facilities/${facility.facility_id}`)
                }
              />
            ))}
          </div>
        )}
      </div>

      <Fab
        draggable
        onClick={() => navigate("/app/facilities/my-bookings")}
        label="My bookings"
      >
        <CalendarDays className="h-5 w-5" strokeWidth={1.9} />
      </Fab>
    </div>
  );
}

function FacilityCard({
  facility,
  onClick,
}: {
  facility: Facility;
  onClick: () => void;
}): JSX.Element {
  const external = facility.booking_mode === "external_link";
  const category = categoryOf(facility) ?? (external ? "External" : "Booking");
  return (
    <Card
      asChild
      noGrain
      className="overflow-hidden rounded-xl p-0 text-left shadow-none transition-transform duration-fast active:scale-[0.98]"
    >
      <button type="button" onClick={onClick} className="flex w-full flex-col">
        <div className="aspect-[4/3] w-full bg-sunken">
          {facility.photo_url ? (
            <img
              src={facility.photo_url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-primary-50">
              <CalendarRange
                className="h-7 w-7 text-primary-600"
                strokeWidth={1.5}
              />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-0.5 p-3">
          <span className="text-[0.625rem] font-semibold uppercase tracking-[0.13em] text-brass">
            {category}
          </span>
          <p className="font-display text-body-lg leading-tight text-ink">
            {facility.name}
          </p>
          {facility.location_name ? (
            <p className="text-caption text-ink-muted truncate">
              {facility.location_name}
            </p>
          ) : null}
        </div>
      </button>
    </Card>
  );
}
